import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { AppState, Platform } from 'react-native';
import axios from 'axios';
import useApi from './api-hook';
import { MysteryShopperClient, UpdateAdvertisementTrackingByCodeDto } from '../api/api.g';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAppSelector } from './store-hook';
import { StringHelper } from '../helpers/stringHelper';
import { LoggedState } from '../models/AuthModelsCommon';
import { trialAdvertisementPreferences, TRIAL_ADVERTISEMENT_KEY_PREFIX } from '../helpers/trialAdvertisementPreferences';
import usePopupSession from './usePopupSession';

export interface updateAdvertisementTrackingDto {
  accountId: string;
  code: string;
  data: UpdateAdvertisementTrackingByCodeDto;
}

const useAdvertisementHook = () => {
  const authStore = useAppSelector((selector) => selector.auth);
  const popupSession = usePopupSession();
  const mysteryShopperApi = useApi(MysteryShopperClient);
  const isTrial = popupSession.isActive && (authStore.logged === LoggedState.Anonymous || authStore.user?.isAnonymous === true);
  const accountId = popupSession.isActive && !isTrial && authStore.idToken ? authStore.user?.uid : undefined;
  const currentAccountId = useRef(accountId);
  currentAccountId.current = accountId;
  const trialPreferences = useSyncExternalStore(
    trialAdvertisementPreferences.subscribe,
    trialAdvertisementPreferences.getSnapshot,
    trialAdvertisementPreferences.getSnapshot,
  );

  const [querySeed, setQuerySeed] = useState<string>(() => StringHelper.generateRandomString());

  useEffect(() => {
    if (!isTrial) return;
    void trialAdvertisementPreferences.load();
    const subscription = AppState.addEventListener('change', state => {
      if (state === 'active') void trialAdvertisementPreferences.load();
    });
    const onStorage = (event: StorageEvent) => {
      if (event.key?.startsWith(TRIAL_ADVERTISEMENT_KEY_PREFIX)) void trialAdvertisementPreferences.load();
    };
    if (Platform.OS === 'web' && typeof window !== 'undefined') window.addEventListener('storage', onStorage);
    return () => {
      subscription.remove();
      if (Platform.OS === 'web' && typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
    };
  }, [isTrial]);

  const updateAdvertisementTrackingMutation = useMutation({
    useErrorBoundary: false,
    mutationFn: (dto: updateAdvertisementTrackingDto) => {
      if (!popupSession.isCurrent() || dto.accountId !== currentAccountId.current) return Promise.resolve();
      return mysteryShopperApi.updateAdvertisementTracking(dto.code, dto.data, mysteryShopperApi.tokenSource?.token);
    },
    onSuccess(_data, dto) {
      if (dto.accountId === currentAccountId.current) setQuerySeed(StringHelper.generateRandomString());
    },
  });

  const getAdListQuery = useQuery({
    enabled: !!accountId,
    queryKey: ['getAdListQuery', accountId, querySeed],
    useErrorBoundary: false,
    queryFn: ({ signal }) => {
      // A previous account's cancellation must not cancel the new account's read.
      const source = axios.CancelToken.source();
      const cancel = () => source.cancel('Advertisement query cancelled');
      signal?.addEventListener('abort', cancel, { once: true });
      return mysteryShopperApi.getAdvertisementTracking(source.token)
        .finally(() => signal?.removeEventListener('abort', cancel));
    },
  });
  const adList = getAdListQuery.data?.items;
  const loaded = isTrial ? trialPreferences.loaded : !!accountId && (getAdListQuery.isSuccess || getAdListQuery.isError);

  const getAdvertisementCallback = useCallback(
    (code: string) => {
      if (!loaded || !popupSession.isCurrent()) return false;
      if (isTrial) return !trialPreferences.codes.includes(code);
      // Failed account reads retain the existing "no tracking info" behavior.
      return !adList?.find((x) => x.code === code)?.doNotShowAgain;
    },
    [isTrial, trialPreferences, adList, loaded, popupSession.isCurrent]
  );

  const dontShowAgain = (code: string) => {
    if (!popupSession.isCurrent()) return;
    if (isTrial) {
      void trialAdvertisementPreferences.optOut(code);
      return;
    }
    if (!accountId) return;
    let dto = new UpdateAdvertisementTrackingByCodeDto();
    dto.doNotShowAgain = true;

    updateAdvertisementTrackingMutation.mutate({
      accountId,
      code: code,
      data: dto,
    });
  };

  return { showAdvertisement: getAdvertisementCallback, dontShowAgain: dontShowAgain, loaded: loaded };
};

export default useAdvertisementHook;
